<script setup lang="ts">

import {ChevronDown, Loader2Icon} from "lucide-vue-next";
import {computed, onMounted, type Ref, ref, watch} from "vue";
import {label} from "../../mixins/translations.js";
import {useAddressStore} from "../../stores/address.ts";
import {useEnrollmentStore} from "../../stores/enrollment.ts";
import FloatingInput from "../../ui/inputs/FloatingInput.vue";
import {useDebounceFn} from "@vueuse/core";

const store = useEnrollmentStore();
const addressStore = useAddressStore();

const errors: Ref<object> = ref({});
const success: Ref<object> = ref({});


const validateField = async (fieldName, formData) => {

    store.invoiceDetails[fieldName] = typeof store.invoiceDetails[fieldName] === 'string'
        ? store.invoiceDetails[fieldName].trim()
        : store.invoiceDetails[fieldName];
    try {
        await store.invoiceDetailsValidation.validateAt(fieldName, formData);

        success.value[fieldName] = true;
        delete errors.value[fieldName];
    } catch (error) {
        errors.value[fieldName] = error.message;
        success.value[fieldName] = false;
    }
}

const isValidated = computed(() => {
    try {
        store.invoiceDetailsValidation.validateSync(store.invoiceDetails);
        return true;
    } catch (error) {
        return false;
    }
})

const save = () => {
    store.saveStep2().then(() => {
        store.openStep2 = false;
    }).catch((errorResponse) => {
        //errors not caught by yup
        errors.value = store.filterLaravelErrors(errorResponse, 'invoice_details');
    });
}

const isAutocompleting: Ref<boolean> = ref(false);
//const autocompleteAddress = () => {

const autocompleteAddress = useDebounceFn(async () => {
    if(store.invoiceDetails.postal && store.invoiceDetails.house_number) {
        isAutocompleting.value = true;
        addressStore.autocompleteAddress(store.invoiceDetails.postal, store.invoiceDetails.house_number)
            .then((response) => {
                // Aborted request — ignore, another one is in flight
                if (response === null) return;

                if (response.street && response.city) {
                    store.invoiceDetails.street = response.street;
                    store.invoiceDetails.city = response.city;

                    success.value.street = true;
                    success.value.city = true;
                    errors.value.street = null;
                    errors.value.city = null;
                }
            })
            .catch((error) => {
            })
            .finally(() => {
                isAutocompleting.value = false;
            });
    } else {
        store.invoiceDetails.street = '';
        success.value.street = false;
        store.invoiceDetails.city = '';
        success.value.city = false;
    }
});

//Reset steps on change
watch(store.invoiceDetails, () => {
    store.currentStep = store.EnrollmentStatus.step_2;
})

onMounted(() => {
    validate(true);

})

const validate = (initial = false) => {
    Object.entries(store.invoiceDetails).forEach(([key, value]) => {
        if(!initial) {
            validateField(key, store.invoiceDetails);
        } else if (value) {
            validateField(key, store.invoiceDetails);
        }
    });
}

const validateAndAutocomplete = (field) => {
    validateField(field, store.invoiceDetails)
    autocompleteAddress();
}
</script>

<template>
    <div class="rounded-md overflow-hidden">
        <div class="flex w-full items-center justify-between bg-white py-4 hidden md:flex">
                <span class="font-bold text-lg text-black">
                 {{ label('site.enrollment.step_2')}}: {{ label('site.enrollment.step_2.header')}}
                </span>
            <ChevronDown :class="{
                'rotate-180': store.openStep2
            }" class="h-5 w-5 text-primary cursor-pointer" v-if="store.currentStep > 1" @click=" store.openStep2 = !store.openStep2" />
        </div>
        <span class="font-bold md:hidden text-lg text-black" v-if="store.openStep2">{{ label('site.enrollment.step_2')}}: {{ label('site.enrollment.step_2.header')}}</span>

    <div class="bg-gray-100 px-6 py-4"  v-show="store.openStep2">
        <p class="mb-6 text-gray-800 text-base leading-relaxed" v-text="label('site.enrollment.step_2.description')">
        </p>

        <div class="grid gap-4 md:grid-cols-2">
            <FloatingInput v-model="store.invoiceDetails.organisation" @focusout="validateField('organisation', store.invoiceDetails)" @change="validateField('organisation', store.invoiceDetails)" :error="errors.organisation" :success="success.organisation" :label="`${label('site.name_organisation')} *`" />
            <FloatingInput type="tel" v-model="store.invoiceDetails.phone" @focusout="validateField('phone', store.invoiceDetails)" @change="validateField('phone', store.invoiceDetails)" :error="errors.phone" :success="success.phone" :label="`${label('site.phone_organisation')} *`" />
            <FloatingInput v-model="store.invoiceDetails.postal" @focusout="validateAndAutocomplete('postal')" @change="validateField('postal', store.invoiceDetails)" :error="errors.postal" :success="success.postal" :label="`${label('site.postal')} *`" />
            <FloatingInput v-model="store.invoiceDetails.house_number" @focusout="validateAndAutocomplete('house_number')" @change="validateField('house_number', store.invoiceDetails)" :error="errors.house_number" :success="success.house_number" :label="`${label('site.house_number')} *`" />

            <FloatingInput v-model="store.invoiceDetails.street" @focusout="validateField('street', store.invoiceDetails)" @change="validateField('street', store.invoiceDetails)" :error="errors.street" :success="success.street" :label="`${label('site.street')} *`" />
            <FloatingInput v-model="store.invoiceDetails.city" @focusout="validateField('city', store.invoiceDetails)" @change="validateField('city', store.invoiceDetails)" :error="errors.city" :success="success.city" :label="`${label('site.city')} *`" />
            <Loader2Icon class="w-5 h-5 animate-spin" v-if="isAutocompleting" />

            <FloatingInput class="md:col-span-2" v-model="store.invoiceDetails.email" @focusout="validateField('email', store.invoiceDetails)" @change="validateField('email', store.invoiceDetails)" :error="errors.email" :success="success.email" :label="`${label('site.invoice_email')} *`" />
            <FloatingInput class="md:col-span-2" v-model="store.invoiceDetails.reference" @focusout="validateField('reference', store.invoiceDetails)" @change="validateField('reference', store.invoiceDetails)" :error="errors.reference" :success="success.reference" :label="`${label('site.invoice_reference')}`" />
        </div>

        <div class="mt-8 flex justify-end">
            <div class="flex flex-col items-end gap-4">
                <label class="flex items-center gap-3 text-sm text-gray-800">
                    <input
                        type="checkbox"
                        class="h-5 w-5 rounded border-gray-300
                   text-primary focus:ring-primary"
                        :class="{
                          'border-primary-300': !store.invoiceDetails?.terms_of_service
                        }"
                        v-model="store.invoiceDetails.terms_of_service"
                        required
                    />
                    <span v-html="label('site.terms_of_service', {
                        'link_start': `<a href='${label('site.terms_of_service.link')}' target='_blank' class='underline font-semibold'>`,
                        'link_end': `</a>`
                    })"></span>
                </label>

                <div class="text-right" v-if="!store.isValidStep2">
                    <span class="text-sm text-primary" v-text="label('site.enrollment.not_all_filled')">
                    </span>
                </div>
                <button @click.prevent="store.isValidStep2 ? save() : validate()" type="button" class="rounded-md bg-primary px-8 py-3 font-semibold text-white hover:bg-primary-400">
                    <Loader2Icon v-if="store.isLoading" class="w-5 h-5 animate-spin " />
                    <span v-else v-text="label('site.enrollment.step_2.button')"></span>
                </button>
            </div>
        </div>
    </div>
    </div>
</template>
